import { useState, memo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Heart, MessageCircle, Share2, MapPin, Flame, Bookmark, MoreHorizontal, Flag } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LazyImage } from '@/components/ui/lazy-image'
import { LikeButton } from '@/components/interaction/like-button'
import { CommentSystem } from '@/components/interaction/comment-system'
import { ShareButton } from '@/components/interaction/share-button'
import ReportSystem from '@/components/interaction/report-system'
import { useInteractionStore } from '@/stores/interaction-store'
import { useAuthStore } from '@/stores/auth-store'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface Post {
  id: string
  userId?: string
  isAnonymous?: boolean
  content: string
  author: {
    name: string
    avatar?: string
    school: string
    level: number
  }
  images?: string[]
  category: string
  location?: string
  createdAt: Date
  likes: number
  comments: number
  hot?: boolean
  price?: string
  tags?: string[]
  // 兼容旧格式
  user?: {
    name: string
    avatar: string
    school: string
    level: number
  }
  time?: string
  isHot?: boolean
}

interface PostCardProps {
  post: Post
  showActions?: boolean
  className?: string
  onLike?: (postId: string) => void
  onComment?: (postId: string) => void
  onShare?: (postId: string) => void
  onBookmark?: (postId: string) => void
}

const PostCard = memo(function PostCard({ 
  post, 
  showActions = true,
  className,
  onLike,
  onComment,
  onShare,
  onBookmark
}: PostCardProps) {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const { 
    likes, 
    comments, 
    shares,
    toggleLike, 
    addComment, 
    likeComment, 
    reportComment,
    fetchComments,
    incrementShare 
  } = useInteractionStore()

  const [isBookmarked, setIsBookmarked] = useState(false)

  // 兼容新旧数据格式
  const author = post.author || post.user || { name: '未知用户', school: '未知学校', level: 1 }

  // 点击用户头像查看个人资料
  const handleUserClick = useCallback(() => {
    const userId = post.userId || (post.author as any)?.id
    // 匿名帖（后端将匿名作者 _id 替换为 'anonymous'）没有个人主页
    if (!userId || userId === 'anonymous') {
      toast.info('匿名用户没有个人主页')
      return
    }
    navigate(`/user/${userId}`)
  }, [post.userId, post.author, navigate])
  const createdAt = post.createdAt || new Date()
  const timeDisplay = post.time || formatDistanceToNow(createdAt, { addSuffix: true, locale: zhCN })
  const isHot = post.hot || post.isHot || false

  const postLikes = likes[post.id] || { count: post.likes || 0, liked: false }
  const postComments = comments[post.id] || []
  const shareCount = shares[post.id] || 0

  const getCategoryColor = useCallback((category: string) => {
    switch (category) {
      case '学习': return 'bg-blue-100 text-blue-700 border-blue-200'
      case '生活': return 'bg-orange-100 text-orange-700 border-orange-200'
      case '娱乐': return 'bg-pink-100 text-pink-700 border-pink-200'
      case '求助': return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      case '交易': return 'bg-purple-100 text-purple-700 border-purple-200'
      case '其他': return 'bg-indigo-100 text-indigo-700 border-indigo-200'
      default: return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }, [])

  const getLevelColor = (level: number) => {
    if (level >= 10) return 'bg-purple-500'
    if (level >= 5) return 'bg-blue-500'
    if (level >= 3) return 'bg-green-500'
    return 'bg-gray-500'
  }

  // 需要登录的操作处理
  const handleAuthRequiredAction = useCallback((action: string, callback: () => void) => {
    if (isAuthenticated) {
      callback()
    } else {
      let message = ''
      switch (action) {
        case 'like':
          message = '点赞需要先登录'
          break
        case 'comment':
          message = '评论需要先登录'
          break
        case 'bookmark':
          message = '收藏需要先登录'
          break
        case 'report':
          message = '举报需要先登录'
          break
        default:
          message = '此操作需要先登录'
      }
      toast.info(message)
      navigate('/login')
    }
  }, [isAuthenticated, navigate])

  const handleLike = useCallback(() => {
    handleAuthRequiredAction('like', () => {
      toggleLike(post.id).catch(err => toast.error(err?.message || '点赞失败'))
      onLike?.(post.id)
    })
  }, [handleAuthRequiredAction, toggleLike, post.id, onLike])

  const handleBookmark = useCallback(() => {
    handleAuthRequiredAction('bookmark', () => {
      setIsBookmarked(!isBookmarked)
      onBookmark?.(post.id)
      toast.success(isBookmarked ? '取消收藏' : '收藏成功')
    })
  }, [handleAuthRequiredAction, isBookmarked, post.id, onBookmark])

  const handleComment = useCallback((content: string, replyTo?: string) => {
    handleAuthRequiredAction('comment', () => {
      addComment(post.id, content, replyTo).catch(err => toast.error(err?.message || '评论失败'))
      onComment?.(post.id)
    })
  }, [handleAuthRequiredAction, addComment, post.id, onComment])

  const handleContactSeller = useCallback(() => {
    handleAuthRequiredAction('contact', () => {
      // 模拟联系卖家功能
      const phone = "联系方式：微信/QQ等";
      alert(`联系卖家\n${phone}\n\n提示：请通过安全渠道进行交易`);
    })
  }, [handleAuthRequiredAction])

  return (
    <div className={cn(
      "bg-white rounded-xl shadow-sm p-4 lg:p-6 space-y-3 lg:space-y-4 card-lift border border-gray-100 hover:border-blue-200/60",
      className
    )}>
      {/* 用户信息 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={cn("rounded-full p-0.5", !post.isAnonymous && "avatar-ring")}>
            <Avatar 
              className="w-10 h-10 ring-2 ring-white cursor-pointer hover:scale-105 transition-transform duration-200"
              onClick={handleUserClick}
            >
              <AvatarImage src={author.avatar} />
              <AvatarFallback className="bg-gradient-to-r from-blue-400 to-purple-400 text-white font-medium">
                {author.name?.[0] || '?'}
              </AvatarFallback>
            </Avatar>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span 
                className="font-medium text-gray-900 hover:text-blue-600 cursor-pointer transition-colors"
                onClick={handleUserClick}
              >
                {author.name}
              </span>
              <Badge 
                variant="secondary" 
                className={cn("text-xs text-white", getLevelColor(author.level))}
              >
                Lv.{author.level}
              </Badge>
              {isHot && (
                <div className="flex items-center space-x-1 text-orange-500 animate-pulse">
                  <Flame className="w-4 h-4" />
                  <span className="text-xs font-medium">热门</span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <span className="hover:text-gray-700 cursor-pointer transition-colors">{author.school}</span>
              <span>•</span>
              <span>{timeDisplay}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Badge className={cn("text-xs border", getCategoryColor(post.category))}>
            {post.category}
          </Badge>
          {showActions && (
            <div className="flex items-center space-x-1">
              <ReportSystem
                targetType="post"
                targetId={post.id}
                targetTitle={post.content.slice(0, 50) + '...'}
                onReportSubmitted={(reportId) => {
                  // 静默处理举报提交
                }}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-gray-400 hover:text-gray-600">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={handleBookmark}>
                    <Bookmark className="w-4 h-4 mr-2" />
                    收藏帖子
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    navigator.clipboard.writeText(`【${post.category}】${post.content.slice(0, 50)}（来自大悟校园墙）`)
                    toast.success('内容已复制')
                  }}>
                    <Share2 className="w-4 h-4 mr-2" />
                    复制链接
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {/* 内容 */}
      <div className="space-y-3">
        <p className="text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
          {post.content}
        </p>
        
        {/* 标签 */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {post.tags.map((tag, index) => (
              <span 
                key={index} 
                className="tag-pill bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-100 cursor-pointer"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
        
        {/* 价格信息 - 交易分类专用 */}
        {post.price && post.category === '交易' && (
          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl font-bold text-green-600">{post.price}</span>
                  <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                    可议价
                  </Badge>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-green-600 border-green-300 hover:bg-green-50"
                  onClick={() => {
                    // 模拟联系卖家功能
                    const phone = "联系方式：微信/QQ等";
                    alert(`联系卖家\n${phone}\n\n提示：请通过安全渠道进行交易`);
                  }}
                >
                  联系卖家
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-gray-500 hover:text-green-600"
                  onClick={handleBookmark}
                >
                  <Heart className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between text-sm text-gray-600">
              <span>💰 支持当面交易，安全可靠</span>
              <span className="text-xs text-gray-500">点击联系卖家了解详情</span>
            </div>
          </div>
        )}

        {/* 图片 */}
        {post.images && post.images.length > 0 && (
          <div className={cn(
            "grid gap-2 rounded-lg overflow-hidden",
            post.images.length === 1 ? "grid-cols-1" : 
            post.images.length === 2 ? "grid-cols-2" :
            post.images.length === 3 ? "grid-cols-3" :
            "grid-cols-2"
          )}>
            {post.images.slice(0, 4).map((image, index) => (
              <div key={index} className="relative group">
                <LazyImage
                  src={image}
                  alt={`图片 ${index + 1}`}
                  className={cn(
                    "w-full object-cover bg-gray-100 rounded-lg cursor-pointer transition-transform duration-200 group-hover:scale-105",
                    post.images!.length === 1 ? "h-64" : "h-32"
                  )}
                />
                {post.images!.length > 4 && index === 3 && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                    <span className="text-white font-medium">+{post.images!.length - 4}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 位置信息 */}
        {post.location && (
          <div className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 cursor-pointer transition-colors">
            <MapPin className="w-4 h-4" />
            <span>{post.location}</span>
          </div>
        )}
      </div>

      {/* 互动按钮 */}
      {showActions && (
        <div className="space-y-4">
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-center space-x-6">
              <LikeButton
                postId={post.id}
                initialLiked={postLikes.liked}
                initialCount={postLikes.count}
                onLike={() => {
                  toggleLike(post.id).catch(err => toast.error(err?.message || '点赞失败'))
                  onLike?.(post.id)
                }}
              />
              
              <ShareButton
                postId={post.id}
                title={post.content.slice(0, 50)}
                content={post.content}
                onShare={(platform) => {
                  incrementShare(post.id, platform)
                  onShare?.(post.id)
                }}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleBookmark}
                className={cn(
                  "transition-colors duration-200",
                  isBookmarked 
                    ? "text-yellow-500 hover:text-yellow-600" 
                    : "text-gray-500 hover:text-yellow-500"
                )}
              >
                <Bookmark className={cn("w-4 h-4", isBookmarked && "fill-current")} />
              </Button>
            </div>
          </div>

          {/* 评论系统 */}
          <CommentSystem
            postId={post.id}
            comments={postComments}
            onOpenComments={() => {
              fetchComments(post.id).catch(err => console.error('加载评论失败:', err))
            }}
            onAddComment={(content, replyTo) => {
              addComment(post.id, content, replyTo).catch(err => toast.error(err?.message || '评论失败'))
              onComment?.(post.id)
            }}
            onLikeComment={(commentId) => {
              likeComment(commentId).catch(err => toast.error(err?.message || '点赞失败'))
            }}
            onReportComment={reportComment}
          />
        </div>
      )}
    </div>
  )
})

export { PostCard }
export type { Post, PostCardProps }